import { Box, Flex, Icon, Image, Link, Text } from '@everlywell/ui-kit';
import { Link as GatsbyLink } from 'gatsby';
import React from 'react';


export type HighlightVisualTheme = 'moonlight' | 'default';

export const HIGHLIGHT_TEST_IDS = {
  card: 'highlight-card',
} as const;

type HighlightThemeTokens = {
  backgroundColor: string;
  headlineColor: string;
  descriptionColor: string;
  iconColor: string;
  focusRingColor: string;
};

export type HighlightProps = {
  image?: string;
  imageAlt?: string;
  headline?: string;
  description?: string;
  link?: string;
  theme?: HighlightVisualTheme;
  showOnPDP?: boolean;
  backgroundColorToken?: string;
};

const DEFAULT_THEME: HighlightVisualTheme = 'moonlight';

export const HIGHLIGHT_THEMES: Record<
  HighlightVisualTheme,
  HighlightThemeTokens
> = {
  moonlight: {
    backgroundColor: 'moonlight.wash',
    headlineColor: 'moonlight.base',
    descriptionColor: 'tints.darkGrey',
    iconColor: 'moonlight.base',
    focusRingColor: 'moonlight.light',
  },
  default: {
    backgroundColor: 'tints.lightCream',
    headlineColor: 'tints.black',
    descriptionColor: 'tints.darkGrey',
    iconColor: 'tints.black',
    focusRingColor: 'viridian.light',
  },
};

const ChevronRightIcon: React.FC<{ color?: string }> = ({ color }) => (
  <Icon
    viewBox="0 0 16 16"
    boxSize="16px"
    marginRight={['18px', '16px']}
    color={color}
  >
    <path
      d="M6 13L11 8L6 3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </Icon>
);

const getThemeTokens = (theme: HighlightVisualTheme): HighlightThemeTokens =>
  HIGHLIGHT_THEMES[theme] ?? HIGHLIGHT_THEMES[DEFAULT_THEME];

const HighlightCardContent: React.FC<
  HighlightProps & { tokens: HighlightThemeTokens }
> = ({ image, imageAlt, headline, description, tokens }) => (
  <Flex
    alignItems="center"
    backgroundColor={tokens.backgroundColor}
    borderRadius="xl"
    flexWrap={['wrap', null, 'nowrap']}
    gap={['4', '3']}
    justifyContent="space-between"
    minHeight={['auto', '92px']}
    paddingX={[0, 0]}
    paddingY={[4, 0]}
    paddingLeft="3px"
    width="100%"
    data-testid={HIGHLIGHT_TEST_IDS.card}
  >
    {image ? (
      <Box
        as="figure"
        margin={0}
        marginTop="calc(0.3 * 4px + 6px)"
        marginLeft={['-10px', '-3px']}
        flexShrink={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Image
          alt={imageAlt ?? ''}
          src={image}
          width="106.449px"
          height="71.339px"
          objectFit="cover"
        />
      </Box>
    ) : null}

    <Flex
      alignItems="center"
      flex={1}
      flexWrap={['wrap', 'nowrap']}
      gap={[3, 4]}
      justifyContent={['center', 'space-between']}
      paddingY={[0, 3]}
      width="100%"
      maxHeight={['auto', '92px']}
    >
      <Box
        flex={1}
        minWidth={0}
        textAlign="left"
        marginLeft="3px"
        marginBottom="2px"
      >
        {headline ? (
          <Text
            as="h3"
            fontFamily="body"
            fontSize={['16px', '18px']}
            fontWeight={500}
            lineHeight="100%"
            color={tokens.headlineColor}
            marginTop={['calc(3 * 4px + -1px)', '0']}
            marginLeft={['1px', '0px']}
          >
            {headline}
          </Text>
        ) : null}

        {description ? (
          <Text
            marginTop={['calc(1.4 * 8px - 10px)', '1.5']}
            marginRight={['3px', '0px']}
            fontFamily="body"
            fontSize={['12px', '14px']}
            lineHeight={['151%', '150%']}
            color={tokens.descriptionColor}
          >
            {description}
          </Text>
        ) : null}
      </Box>

      <Box
        display="flex"
        flexShrink={0}
        alignItems="center"
        justifyContent="center"
        color={tokens.iconColor}
        width="32px"
        height="32px"
      >
        <ChevronRightIcon color={tokens.iconColor} />
      </Box>
    </Flex>
  </Flex>
);

const Highlight: React.FC<HighlightProps> = (props) => {
  const {
    image,
    imageAlt,
    headline,
    description,
    link,
    theme = DEFAULT_THEME,
    backgroundColorToken,
  } = props;

  if (!headline && !description) {
    return null;
  }

  const baseTokens = getThemeTokens(theme);
  const tokens = {
    ...baseTokens,
    backgroundColor: backgroundColorToken ?? baseTokens.backgroundColor,
  };
  const highlightCard = (
    <HighlightCardContent
      tokens={tokens}
      image={image}
      imageAlt={imageAlt}
      headline={headline}
      description={description}
    />
  );

  if (link) {
    const isExternalLink = /^https?:\/\//.test(link);

    return (
      <Link
        {...(isExternalLink
          ? {
              href: link,
              isExternal: true,
              rel: 'noopener noreferrer',
            }
          : {
              as: GatsbyLink,
              to: link,
            })}
        display="block"
        borderRadius="xl"
        _hover={{ textDecoration: 'none' }}
      >
        {highlightCard}
      </Link>
    );
  }

  return highlightCard;
};

export default Highlight;
